import angular from 'angular';
import moment from 'moment';
import DocumentService from '@/shared/services/DocumentService';
import SignalRService from '@/shared/services/SignalRService';
import { isWord, isPdf, isExcel } from '@/shared/utils/mime';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { isDutchTenantConfiguration } from '@/shared/TenantConfigurationConstants';
import dataCacheService from '@/shared/services/DataCacheService';
import AuthService from '@/shared/services/AuthService';

const amlDocumentTypes = [10, 24, 25, 26, 27, 32, 82, 92, 104, 123, 203, 250];
const amlDocumentTypesNL = [10, 24, 25, 26, 27, 80, 82, 92, 104, 123, 136, 203, 250, 251, 252];

const amlDocumentTypesNoSignatureNeeded = [136];

const ensureDate = value => {
  if (!value) return null;
  if (typeof value === 'string') return new moment(value.substr(0, 10)).toDate();
  if (moment.isMoment(value)) return value.toDate();
  if (value instanceof Date) return value;
  return value;
};
const mapDocument = ({
  createDate,
  documentDate,
  documentStartDate,
  documentEndDate,
  mime,
  ...other
}) => ({
  ...other,
  mime,
  createDate: ensureDate(createDate),
  documentDate: ensureDate(documentDate),
  documentStartDate: ensureDate(documentStartDate),
  documentEndDate: ensureDate(documentEndDate),
  isPdf: isPdf(mime),
  isWord: isWord(mime),
  isExcel: isExcel(mime),
});

const consolidateDocuments = documents => {
  return documents
    .filter(x => {
      if (!x.scannedDocumentId) return true;
      if (x.id === x.scannedDocumentId) return true;
      return !documents.some(y => y.id === x.scannedDocumentId);
    })
    .map(mapDocument);
};

angular.module('cxRelations').directive('cxRelAml', [
  function() {
    return {
      restrict: 'E',
      //replace: true,
      template: require('./relAml.html'),
      scope: {
        data: '=',
        field: '=',
        relation: '=',
        saveRem: '&save',
        disabled: '=?',
      },
      controller: [
        '$scope',
        '$q',
        'ModalService',
        '$http',
        'appUrls',
        'flySvc',
        'dialogsSvc',
        '$timeout',
        '$translate',
        'dataCache',
        '$rootScope',
        'translateListFilter',
        'checkSecurity',
        'appMessages',
        function(
          $scope,
          $q,
          ModalService,
          $http,
          appUrls,
          flySvc,
          dialogsSvc,
          $timeout,
          $translate,
          dataCache,
          $rootScope,
          translateListFilter,
          checkSecurity,
          appMessages,
        ) {
          $scope.forms = {
            otherDocumentsRemarkForm: null,
          };
          $scope.documents = [];
          $scope.translatedDocumentTypes = [];
          $scope.documentTypes = amlDocumentTypes;
          $scope.documentTypesNoSignatureNeeded = amlDocumentTypesNoSignatureNeeded;
          $scope.isDutchTenantConfiguration = false;
          $scope.statusDateOptions = {
            startingDay: 1,
            maxDate: moment
              .utc()
              .startOf('day')
              .toDate(),
          };

          const tenantConfiguration = dataCache.getArray('tenantConfiguration');
          if (tenantConfiguration === 5) {
            $scope.documentTypes = amlDocumentTypesNL;
            $scope.isDutchTenantConfiguration = true;
          }
          $scope.translateAndSortDocumentTypes = function() {
            $scope.translatedDocumentTypes = $scope.documentTypes
              .map(x => ({
                id: x,
                label: translateListFilter(x, 'DocumentType'),
              }))
              .sort(dynamicSort('label'));
          };
          $scope.translateAndSortDocumentTypes();

          const settings = dataCache.getCompanySettings($scope.relation.companyId);
          $scope.hasSigningProvider = settings && settings.documentSigningProvider !== 0;

          $scope.revisionRequired = false;
          $scope.$watch('data', function(newValue, oldValue) {
            $scope.exemptions = [
              {
                exemption: 0,
                checked: false,
                date: null,
                user: null,
              },
            ];
            $scope.revisionRequired = false;
            if (newValue && newValue.exemptions) {
              var smallBusinessExemption = newValue.exemptions.find(x => x.exemption === 0);
              if (smallBusinessExemption) {
                angular.extend($scope.exemptions[0], smallBusinessExemption, {
                  checked: true,
                });
              }

              const oldExemptions = newValue.exemptions.filter(x => x.exemption !== 0);
              for (var i = 0; i < oldExemptions.length; i++) {
                $scope.exemptions.push({ ...oldExemptions[i], checked: true });
              }
            }

            if (newValue.expiryDate) {
              const expiryDate = moment(newValue.expiryDate);
              const now = moment();
              if (now.isAfter(expiryDate)) {
                $scope.revisionRequired = true;
              }

              if (now.add(30, 'days').isAfter(expiryDate)) {
                $scope.revisionRequired = true;
              }
            }
          });

          const documentTagAccessUpdatedHandler = tagAccessInfo => {
            if (
              tagAccessInfo.noOneHasAccess ||
              (tagAccessInfo.usersWithAccess?.length > 0 &&
                tagAccessInfo.usersWithAccess.indexOf(AuthService.getUserId()) < 0)
            ) {
              var document = $scope.documents.find(x => x.id === tagAccessInfo.documentId);
              if (document) {
                var index = $scope.documents.indexOf(document);
                if (index >= 0) $scope.documents.splice(index, 1);
              }
            }
          };

          $scope.saveExemptions = function(ex, checked) {
            if (ex.exemption > 0) {
              // cant edit old AML data
              return;
            }
            var checkedEx = $scope.exemptions.filter(function(el) {
              return ex.exemption === el.exemption ? checked : el.checked;
            });
            return $scope.save('exemptions', checkedEx);
          };
          $scope.markRevisionOk = function() {
            $scope.markingRevisionOk = true;
            $http
              .put(appUrls.api + '/api/relation/MarkAssessmentOk?relationId=' + $scope.relation.id)
              .then(
                function(result) {
                  $scope.markingRevisionOk = false;
                  $scope.data = result.data;
                },
                function(error) {
                  $scope.markingRevisionOk = false;
                  flySvc.error(error.code, error.parameters);
                },
              );
          };
          $scope.save = function(field, $data) {
            $scope.saving = true;

            var model = angular.copy($scope.data);

            if (field === 'eidValue') {
              if (!model.eid) model.eid = {};
              model.eid.value = $data;
            } else if (field === 'eidExpiryDate') {
              if (!model.eid) model.eid = {};
              model.eid.expiryDate = $data;
            } else if (field === 'passportValue') {
              if (!model.passport) model.passport = {};
              model.passport.value = $data;
            } else if (field === 'passportExpiryDate') {
              if (!model.passport) model.passport = {};
              model.passport.expiryDate = $data;
            } else {
              model[field] = $data;
            }
            if (
              field === 'customerRisk' ||
              field === 'activityRisk' ||
              field === 'locationRisk' ||
              field === 'transactionRisk'
            ) {
              model.totalRisk = Math.max(
                model.customerRisk,
                model.activityRisk,
                model.locationRisk,
                model.transactionRisk,
              );
            }

            if (angular.equals($scope.data, model)) return false;
            return $q.when(
              $scope.saveRem({
                newValue: model,
              }),
              function(result) {
                $scope.saving = false;
                if (result) {
                  $scope.data = result;
                }

                if (field === 'otherDocuments' && $data) {
                  try {
                    $('#AmlOtherDocumentsRemark')
                      .scope()
                      .$form.$show();
                  } catch (e) {}
                }
                if (field === 'juristicStructure' && $data) {
                  try {
                    $('#AmlJuristicStructureRemark')
                      .scope()
                      .$form.$show();
                  } catch (e) {}
                }
                return false;
              },
              function() {
                $scope.saving = false;
                return '';
              },
            );
          };
          $scope.generateDocuments = function() {
            ModalService.showModal({
              template: require('../dialogs/amlDocumentDialog.html'),
              controller: 'AmlDocumentDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                relation: $scope.relation,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });
              modal.close.then(function(result) {
                if (result && result.length) {
                  $scope.documents = [...$scope.documents, ...result.map(mapDocument)];
                }
              });
            });
          };

          $scope.generateDetailedRiskDocument = function() {
            ModalService.showModal({
              template: require('../dialogs/amlSingleDocumentDialog.html'),
              controller: 'AmlSingleDocumentDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                relation: $scope.relation,
                templateType: 19,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });
              modal.close.then(function(result) {
                if (result) {
                  $scope.documents = [...$scope.documents, mapDocument(result)];
                }
              });
            });
          };

          $scope.generateClientAcceptanceQuestionnaire = function() {
            ModalService.showModal({
              template: require('../dialogs/amlSingleDocumentDialog.html'),
              controller: 'AmlSingleDocumentDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                relation: $scope.relation,
                templateType: 20,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });
              modal.close.then(function(result) {
                if (result) {
                  $scope.documents = [...$scope.documents, mapDocument(result)];
                }
              });
            });
          };

          $scope.generateClientContinuationQuestionnaire = function() {
            ModalService.showModal({
              template: require('../dialogs/amlSingleDocumentDialog.html'),
              controller: 'AmlSingleDocumentDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                relation: $scope.relation,
                templateType: 28,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });
              modal.close.then(function(result) {
                if (result) {
                  $scope.documents = [...$scope.documents, mapDocument(result)];
                }
              });
            });
          };

          $scope.retrieveDocuments = function() {
            if (!checkSecurity('Documents:r')) return;
            $http
              .get(`${appUrls.documents}/api/Documents/Links/${$scope.relation.id}/3/`, {
                params: { documentTypes: $scope.documentTypes },
              })
              .then(function(result) {
                $scope.documents = consolidateDocuments(result.data);
              });
          };
          $scope.retrieveDocuments();

          $scope.onPreviewClosed = () => {
            $timeout(function() {
              $scope.showPreview = false;
              $scope.activeDocumentId = null;
            }, 0);
          };

          $scope.onPreviewSaved = document => {
            $timeout(function() {
              $scope.showPreview = false;
              $scope.activeDocument = null;
              $scope.documents = $scope.documents.map(x => {
                if (x.id === document.id) {
                  return {
                    ...x,
                    ...document,
                  };
                }
                return x;
              });
            }, 0);
          };

          $scope.previewDocument = document => {
            $scope.activeDocument = document;
            $scope.showPreview = true;
          };

          $scope.downloadDocument = function(document, asPdf) {
            const { id: documentId, name, mime } = document;
            if (!documentId) return;

            if (!asPdf || isPdf(mime)) {
              //Normal download
              document.showSpinner = true;
              DocumentService.download(documentId).finally(() => {
                $timeout(function() {
                  document.showSpinner = false;
                }, 0);
              });
            } else {
              document.showSpinner = true;
              //Convert to pdf
              DocumentService.convertToPdf(documentId, name).finally(() => {
                $timeout(function() {
                  document.showSpinner = false;
                }, 0);
              });
            }
          };

          $scope.uploading = false;
          $scope.uploadDocuments = function(files, ignoreUploading) {
            if (!ignoreUploading && $scope.uploading) return;
            if (!files || files.length < 1 || !$scope.relation.id) return;
            $scope.uploading = true;

            var data = new FormData();
            files.forEach(file => {
              data.append('data', file.data);
            });

            $http({
              method: 'post',
              url: `${appUrls.documents}/api/Documents/`,
              params: {
                documentType: 24,
                documentLinkType: 3,
                linkId: $scope.relation.id,
              },
              headers: {
                'Content-Type': undefined,
              },
              data: data,
            })
              .then(function(response) {
                var result = response.data;
                if (!result) return;

                $scope.documents = [...$scope.documents, ...result.map(mapDocument)];
                flySvc.success();
                $scope.uploading = false;
              })
              .catch(function(error) {
                flySvc.error(error);
                $scope.uploading = false;
              });
          };

          $scope.replaceDocument = function(files, document) {
            if (!files || files.length < 1) return;

            var data = new FormData();
            data.append('data', files[0].data);

            document.showSpinner = true;
            $http({
              method: 'post',
              url: `${appUrls.documents}/api/Documents/Replace`,
              params: { documentId: document.id },
              headers: {
                'Content-Type': undefined,
              },
              data: data,
            })
              .then(function(response) {
                var result = response.data;
                if (!result) return;
                angular.extend(document, mapDocument(result));
                flySvc.success();
              })
              .catch(function(error) {
                flySvc.error(error);
              })
              .finally(function() {
                document.showSpinner = false;
              });
          };
          $scope.editDocument = function(document) {
            const language = $translate.use().split('-')[0];
            DocumentService.edit(document.id, language);
          };
          $scope.openInApp = function(document) {
            DocumentService.openInApp(document.id);
          };
          $scope.deleteDocument = function(document) {
            dialogsSvc.showYesNo('comfirmDeleteTitle', 'msgConfirmDeleteDocument').then(function() {
              document.showSpinner = true;
              $http.delete(`${appUrls.documents}/api/Documents/${document.id}`).then(
                function() {
                  flySvc.success();
                  var index = $scope.documents.indexOf(document);
                  if (index >= 0) $scope.documents.splice(index, 1);
                },
                function(error) {
                  flySvc.error(error);
                  document.showSpinner = false;
                  return '';
                },
              );
            });
          };

          $scope.deleteSelectedDocuments = function() {
            const selectedDocuments = $scope.documents.filter(x => x.selected);
            if (!selectedDocuments || selectedDocuments.length === 0) return;

            dialogsSvc
              .showYesNo('comfirmDeleteTitle', 'msgConfirmDeleteDocuments')
              .then(function() {
                selectedDocuments.map(document => {
                  document.showSpinner = true;
                  $http.delete(`${appUrls.documents}/api/Documents/${document.id}`).then(
                    function() {
                      var index = $scope.documents.indexOf(document);
                      if (index >= 0) $scope.documents.splice(index, 1);
                    },
                    function(error) {
                      flySvc.error(error);
                      document.showSpinner = false;
                      return '';
                    },
                  );
                });
              });
          };

          $scope.editDocumentType = function(document, newType) {
            if (typeof newType === 'undefined') return false;
            if (typeof newType === 'string') newType = parseInt(newType);
            if (amlDocumentTypesNL.indexOf(newType) < 0) return false; //Only allow aml document types

            const model = angular.copy(document);
            model.documentType = newType;

            return $http.put(`${appUrls.documents}/api/Documents/Update`, model).then(
              function(result) {
                if (result && result.data) {
                  document.documentType = result.data.documentType;
                }
                return false;
              },
              function(error) {
                flySvc.error(error);
                return '';
              },
            );
          };

          $scope.convertToPdf = document => DocumentService.convertToPdf(document.id);

          $scope.canCheckout = function(document) {
            return (
              dataCacheService.hasUserConnectPlatform(46) &&
              !!document &&
              document.storageType !== 1
            );
          };

          $scope.canCheckin = function(document) {
            return (
              dataCacheService.hasUserConnectPlatform(46) &&
              document.checkedOutByUserId == AuthService.getUserId() &&
              document.storageType !== 1
            );
          };

          $scope.canDiscardCheckout = function(document) {
            return (
              ((dataCacheService.hasUserConnectPlatform(46) &&
                document.checkedOutByUserId == AuthService.getUserId()) ||
                AuthService.hasPermission({ functionality: 'CompanyOwn', privilege: 'Update' })) &&
              document.storageType !== 1
            );
          };

          $scope.checkinDocument = function(document) {
            if (document.checkedOutByUserId == AuthService.getUserId()) {
              checkin(document);
            }
          };

          $scope.onDiscardCheckout = function(document) {
            dialogsSvc
              .showYesNo('comfirmTitle', 'msgDiscardCheckout', {
                name: dataCacheService.getUserName(document.checkedOutByUserId),
              })
              .then(function() {
                discardCheckout(document);
              });
          };

          $scope.getCheckedoutTitle = function(document) {
            if (!document) {
              return '';
            }
            return appMessages('msgCheckedOutBy', {
              name: dataCacheService.getUserName(document.checkedOutByUserId),
            });
          };

          $scope.openEditing = function(document) {
            DocumentService.getCheckedOutLink(document.id).then(link => {
              if (!link) return;
              window.open(link);
            });
          };

          $scope.discardCheckout = function(document) {
            document.showSpinner = true;
            DocumentService.discardCheckout(document.id).then(
              () => {
                document.checkedOutByUserId = null;
                flySvc.success();
                document.showSpinner = false;
              },
              error => {
                console.error(error);
                if (error.response && error.response.data && error.response.data.code) {
                  flySvc.error(error);
                  document.showSpinner = false;
                } else {
                  flySvc.success();
                }
                log.error('error discarding checkout ', error);
              },
            );
          };

          const checkin = function(document) {
            document.showSpinner = true;
            DocumentService.checkin(document.id).then(
              () => {
                document.checkedOutByUserId = null;
                flySvc.success();
                document.showSpinner = false;
              },
              error => {
                console.error(error);
                if (error.response && error.response.data && error.response.data.code) {
                  flySvc.error(error);
                  document.showSpinner = false;
                } else {
                  flySvc.success();
                }
                log.error('error checking in document ', error);
              },
            );
          };

          $scope.checkoutDocument = function(document, openinApp) {
            if (!document || !document.canEdit) return;
            document.showSpinner = true;
            DocumentService.checkout(document.id, openinApp).then(
              data => {
                if (!!data) {
                  document.checkedOutByUserId = AuthService.getUserId();
                  flySvc.success();
                }
                document.showSpinner = false;
              },
              error => {
                console.error(error);
                if (error.response && error.response.data && error.response.data.code) {
                  flySvc.error(error);
                  document.showSpinner = false;
                } else {
                  flySvc.success();
                }
                log.error('error checking out document ', error);
              },
            );
          };

          $scope.markSigned = function(document) {
            if (document.signingStatus >= 10) return; //Already signed or pending
            document.showSpinner = true;
            const model = {
              documentId: document.id,
              signingStatus: 20, // SigningComplete
              signingProvider: 0, // None
            };
            return $http.put(appUrls.documents + '/api/Documents/Sign', model).then(
              function(result) {
                if (result && result.data) {
                  angular.extend(document, mapDocument(result.data));
                  flySvc.success();
                }
                document.showSpinner = false;
                return false;
              },
              function(error) {
                flySvc.error(error);
                document.showSpinner = false;
                return '';
              },
            );
          };

          $scope.openEmailDocumentDialog = function(document, asPdf) {
            ModalService.showModal({
              template: require('../dialogs/mailDocumentDialog.html'),
              controller: 'MailDocumentDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                document: document,
                asPdf: asPdf,
                relationId: $scope.relation.id,
                companyId: $scope.relation.companyId,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });
              modal.close.then(function(result) {
                if (result) {
                  angular.extend(document, result);
                }
              });
            });
          };
          $scope.revokeSignatureRequest = function(document) {
            if (!$scope.hasSigningProvider) {
              flySvc.error('MsgNoSigningProviderConfiguredSeeSettings');
              return;
            }

            dialogsSvc.showYesNo('comfirmTitle', 'msgConfirmRevokeSignature').then(function() {
              document.showSpinner = true;
              let url =
                appUrls.documentSigning +
                `/api/RevokeDocumentSigningRequest/${$scope.relation.companyId}/${document.signingId}/`;
              if (document.signingPackageId) {
                url += `${document.signingPackageId}`;
              }
              $http.post(url).then(
                function(result) {
                  if (result && result.data) {
                    document.showSpinner = false;
                    flySvc.success('msgDocumentSingingRequestRevoked');
                  }
                },
                function(error) {
                  document.showSpinner = false;
                  flySvc.error(error);
                },
              );
            });
          };

          $scope.openSignatureDialog = function(documents) {
            // use direct reference on datacache to prevent not being able to sign a document when provider was just configured
            var hasSigningProvider =
              dataCache.getCompanySettings($scope.relation.companyId).documentSigningProvider !== 0;

            if (!hasSigningProvider) {
              flySvc.error('MsgNoSigningProviderConfiguredSeeSettings');
              return;
            }

            ModalService.showModal({
              template: require('../dialogs/requestDocumentSignatureDialog.html'),
              controller: 'RequestDocumentSignatureDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                documents: documents,
                companyId: $scope.relation.companyId,
                relationId: $scope.relation.id,
                languageCode: $scope.relation.languageCode,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });
              modal.close.then(function(result) {
                if (!result) return;
                // signing request sent successfully, update document status.

                documents.map(x => {
                  angular.extend(x, {
                    ...x,
                    signingStatus: 10,
                  });
                });
              });
            });
          };

          $scope.openSignatureDialogForSelectedDocuments = function() {
            const selectedDocuments = $scope.documents.filter(x => x.selected);
            if (!selectedDocuments || selectedDocuments.length === 0) return;

            if (
              selectedDocuments.some(
                x => x.signingStatus == 10 || x.signingStatus == 20 || x.signingStatus == 21,
              )
            ) {
              flySvc.error('selectionContainsDocumentPendingOrAlreadySigned');
              return;
            }
            return $scope.openSignatureDialog(selectedDocuments);
          };

          const documentUpdatedHandler = document => {
            if (!document) return;
            $timeout(function() {
              var idx = $scope.documents.findIndex(x => x.id == document.id);

              if (idx >= 0) {
                if (
                  document.scannedDocumentId &&
                  $scope.documents.some(x => x.id === document.scannedDocumentId)
                ) {
                  $scope.documents.splice(idx, 1); //Remove original document from list
                  return;
                }
                angular.extend($scope.documents[idx], mapDocument(document));
              }
            }, 0);
          };

          const documentCheckedoutHandler = documentEditLink => {
            window.open(documentEditLink);
          };

          $scope.selectedCount = function() {
            if (!$scope.documents) return 0;
            const count = $scope.documents.reduce(
              (cnt, document) => cnt + (document.selected ? 1 : 0),
              0,
            );

            return count;
          };

          $scope.update = function(document, field, value) {
            if (field === 'documentDate' && !value) return ''; //Document date is required
            if (
              (field === 'documentStartDate' || field === 'documentEndDate') &&
              typeof value === 'undefined'
            )
              return '';

            var model = angular.copy(document);

            if (angular.equals(model[field], value)) return true;

            model[field] = value;

            if (model.documentDate instanceof Date) model.documentDate = model.documentDate.ISO();
            if (model.documentStartDate instanceof Date)
              model.documentStartDate = model.documentStartDate.ISO();
            if (model.documentEndDate instanceof Date)
              model.documentEndDate = model.documentEndDate.ISO();

            return $http.put(appUrls.documents + '/api/Documents/Update', model).then(
              function(result) {
                if (result && result.data) {
                  angular.extend(document, mapDocument(result.data));
                  flySvc.success();
                }
                return false;
              },
              function(error) {
                flySvc.error(error);
                return '';
              },
            );
          };

          SignalRService.subscribe('documentUpdated', documentUpdatedHandler);
          SignalRService.subscribe('documentSigningStatusUpdated', documentUpdatedHandler);
          SignalRService.subscribe('checkoutFinished', documentCheckedoutHandler);
          SignalRService.subscribe('documentTagAccessUpdated', documentTagAccessUpdatedHandler);

          $scope.$on('$destroy', function() {
            SignalRService.unsubscribe('documentUpdated', documentUpdatedHandler);
            SignalRService.unsubscribe('documentSigningStatusUpdated', documentUpdatedHandler);
            SignalRService.unsubscribe('checkoutFinished', documentCheckedoutHandler);
            SignalRService.unsubscribe('documentTagAccessUpdated', documentTagAccessUpdatedHandler);
          });
          const deregistration = $rootScope.$on(
            '$translateChangeSuccess',
            $scope.translateAndSortDocumentTypes,
          );
          $scope.$on('$destroy', deregistration);
        },
      ],
    };
  },
]);
